// ────────────────────────────────────────────────────────────────────────────
//  usePayroll.ts — Lohnbuchhaltung & Zeit-Analyse
//
//  Worker:
//    GET /punch/team/stats?period=week|month|quarter|year
//      → org-weite Auswertung (orgTotal, perMember[], …) — gated:
//        Owner/Admin/can_view_salaries
//    GET /org/employees → Mitarbeiter-Profile mit hourlyRate/monthlySalary
//    PUT /org/employees/:userId — Profile updaten
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react'
import { api, APIError } from './api'

export interface TeamStatsMember {
  userId:         string
  displayName:    string
  email:          string | null
  avatarUrl:      string | null
  totalSeconds:   number
  pauseSeconds:   number
  pauseCount:     number
  daysWorked:     number
  isClockedInNow: boolean
}

export interface TeamStats {
  period: string
  from:   string
  to:     string
  employees: number
  orgTotalSeconds:      number
  orgTotalPauseSeconds: number
  orgPauseCount:        number
  orgWorkDays:          number
  orgAverageSecondsPerEmployee: number
  perMember: TeamStatsMember[]
}

export interface Employee {
  memberId:        number
  userId:          string
  displayName:     string
  email:           string | null
  role:            string
  positionId:      number | null
  positionTitle:   string | null
  departmentId:    number | null
  departmentName:  string | null
  hourlyRate:      number | null
  monthlySalary:   number | null
  weeklyHours:     number | null
  hireDate:        string | null
  profileNotes:    string | null
  salaryVisibleToCaller: boolean
}

export type ForbiddenReason = 'not-allowed' | 'no-access' | null

// ── Team-Stats ──────────────────────────────────────────────────────────────
export function useTeamStats(period: 'week'|'month'|'quarter'|'year' = 'month') {
  const [stats, setStats] = useState<TeamStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState<ForbiddenReason>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true); setForbidden(null); setError(null)
    try {
      const r = await api.get<TeamStats>(`/punch/team/stats?period=${period}`)
      setStats(r)
    } catch (e: any) {
      if (e instanceof APIError && e.status === 403) {
        setForbidden('not-allowed')
      } else {
        setError(e?.message ?? 'load failed')
      }
    } finally { setLoading(false) }
  }, [period])

  useEffect(() => { void refresh() }, [refresh])

  return { stats, loading, forbidden, error, refresh }
}

// ── Mitarbeiter-Profile ─────────────────────────────────────────────────────
export function useEmployees() {
  const [items, setItems] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get<Employee[]>('/org/employees')
      setItems(Array.isArray(r) ? r : [])
      setError(null)
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  return { items, loading, error, refresh }
}

export async function updateEmployeeProfile(userId: string, opts: {
  positionId?:    number | null
  departmentId?:  number | null
  hourlyRate?:    number | null
  monthlySalary?: number | null
  weeklyHours?:   number | null
  hireDate?:      string | null
  profileNotes?:  string | null
}): Promise<void> {
  await api.put(`/org/employees/${userId}`, opts)
}

// ── Helpers ────────────────────────────────────────────────────────────────
export function formatHours(seconds: number): string {
  const h = seconds / 3600
  return h.toFixed(2).replace('.', ',')
}

export function calcGross(seconds: number, hourlyRate: number | null): number | null {
  if (hourlyRate == null) return null
  const h = seconds / 3600
  return Math.round(h * hourlyRate * 100) / 100
}
